import React from "react";

import { useList } from "@refinedev/core";

import {
  GROUP_DATES_PAGE_SIZE,
  type GroupDatesState,
  type MemberEventDocument,
  memberFilterIds,
  rangesByGroup,
} from "@/pages/event-groups/lib/group-dates";
import type { IHit } from "@/types/common";

/** The Dates cell reads this; a cell outside the groups table sees no dates. */
export const GroupDatesContext = React.createContext<GroupDatesState>({
  status: "error",
});

/**
 * The date range of every group on the page, from ONE `/events/explore` request: the member
 * events of all the visible groups (`group.id:[a,b,...]`), only the fields the range needs. Runs
 * once the page of groups has loaded and again when the page changes. When the members outnumber
 * one explore page the ranges would be partial, so that reads as an error: a dash, never a wrong
 * date. A failed request is a dash too and never touches the table.
 */
export const useGroupDates = (groupIds: string[]): GroupDatesState => {
  const ids = memberFilterIds(groupIds);
  const { query, result } = useList<IHit<MemberEventDocument>>({
    resource: "events",
    pagination: { currentPage: 1, pageSize: GROUP_DATES_PAGE_SIZE },
    filters: [{ field: "group.id", operator: "in", value: ids }],
    meta: {
      query: "*",
      includeFields: ["id", "group", "startDate", "timezone"],
    },
    queryOptions: { enabled: ids.length > 0 },
    errorNotification: false,
  });

  return React.useMemo<GroupDatesState>(() => {
    if (ids.length === 0) return { status: "ready", ranges: {} };
    if (query.isError) return { status: "error" };
    if (!query.data) return { status: "loading" };
    const hits = result.data ?? [];
    if ((result.total ?? hits.length) > hits.length) return { status: "error" };
    return {
      status: "ready",
      ranges: rangesByGroup(hits.map((h) => h.document)),
    };
    // `ids` is rebuilt every render; its content is what the query keys on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(","), query.isError, query.data, result]);
};
